/**
 * Outage Component - Device outage analysis and reporting page
 * 
 * Features:
 * - Outage device data visualization with charts and tables
 * - Site/Location filtering with cascading dropdowns
 * - Date range filtering (up to 6 months historical data)
 * - KPI statistics for outage analysis
 * - CSV export functionality for outage reports
 * - Real-time outage status monitoring
 * - Offline events tracking and analysis
 * 
 * @author CTS - Ford PM UI Team
 
 */

import React, { useMemo, useState, useEffect, useRef } from 'react';
import { getOutageDevices, getOutageCount } from '../../api/outageApi';
import { getSitesApi } from '../../api/dashboardApi';
import { FORMAT_TYPES, formatOutageDuration, parseOutageDurationToMinutes, formatMinutesToDuration } from '../../utils/durationUtils.jsx';

// import OutageDistributionChart from './dountChart.jsx';
import OutageTable from './OutageTable.jsx';
import Kpi from '../../components/Kpi.jsx';

// Material UI imports
import {
  Typography,
  Select,
  Box,
} from '@mui/material';

import {
  Search as SearchIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

// Custom styled components
import {
  OutageContainer,
  OutageOuterContainer,
  OutagePageHeader,
  OutagePanel,
  OutageFiltersPanel,
  OutageFiltersRow,
  OutageFiltersLeft,
  OutageSelect,
  OutageMenuItem,
  OutagePrimaryButton,
  OutageRangeText,
  OutageKpisContainer,
  OutageLoadingMessage,
  OutageErrorMessage,
  OutageFilterLabel,
  OutageShellyHeader,
  OutageShellyLeftText, 
  OutageShellyRightKpis
} from './Outage.styles';
import { AppLabels, getEnv } from '../../constant/constant';
import { useAuth } from '../../context/AuthContext';

/**
 * Generates outage search context display text based on current selection
 * @param {string} selectedSite - Currently selected site ID ('all' or specific site)
 * @param {string} selectedLocation - Currently selected location ID ('all' or specific location)
 * @param {Array} apiSites - Array of site objects from sitesData.AllSites
 * @param {Array} availableLocations - Array of available location objects
 * @param {number} deviceCount - Total number of devices in current view
 * @returns {string} Formatted context text
 */
const getOutageSearchContextText = (selectedSite, selectedLocation, apiSites, availableLocations, deviceCount) => {
  if (selectedSite === 'all' && selectedLocation === 'all') {
    return `Viewing Outage Data Across All Sites (${deviceCount} Devices)`;
  }

  if (selectedSite !== 'all' && selectedLocation === 'all') {
    const siteName = apiSites.find(s => s.store_id === selectedSite)?.store_name || 'Unknown Site';
    return `Viewing Outage Data In: ${siteName} (${deviceCount} devices)`;
  }

  if (selectedSite !== 'all' && selectedLocation !== 'all') {
    const siteName = apiSites.find(s => s.store_id === selectedSite)?.store_name || 'Unknown Site';
    const locationName = availableLocations.find(l => l.location_id === selectedLocation)?.location_name || 'Unknown Location';
    return `Viewing Outage Data In: ${siteName} → ${locationName} (${deviceCount} devices)`;
  }

  if (selectedLocation !== 'all') {
    const locationName = availableLocations.find(l => l.location_id === selectedLocation)?.location_name || 'Unknown Location';
    return `Viewing Outage Data In: ${locationName} (${deviceCount} devices)`;
  }

  return `Loading Outage Context... (${deviceCount} devices)`;
};

export default function Outage() {
  // Ref to prevent duplicate API calls
  const isFetchingSitesRef = useRef(false);

  // API Data
  const [sitesData, setSitesData] = useState({ AllSites: [] });
  const [devicesData, setDevicesData] = useState({ devices: [] });
  const [outageCountData, setOutageCountData] = useState({ sites : [] });
  const [loading, setLoading] = useState(true);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [siteId, setSiteId] = useState('all');
  const [locationId, setLocationId] = useState('all');

  const { user } = useAuth(); // Current authenticated user
  
  // Check if current environment is non-prod (local or qa)
  const isNonProdEnvironment = () => {
    const env = getEnv();
    return env === 'local' || env === 'qa';
  };

  // Track the actual search parameters used for current results
  const [currentSearchParams, setCurrentSearchParams] = useState({
    siteId: 'all',
    locationId: 'all'
  });

  // Date range (default last 7 days)
  const today = new Date();
  const defaultFrom = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  const [from, setFrom] = useState(defaultFrom.toISOString().slice(0, 10)); // yyyy-mm-dd
  const [to, setTo] = useState(today.toISOString().slice(0, 10));

  // Date restrictions
  // Max date for "To" field is today
  const maxToDate = today.toISOString().slice(0, 10);
  // Min date for "From" field is 6 months back from today
  const minFromDate = new Date(today.getFullYear(), today.getMonth() - 6, today.getDate()).toISOString().slice(0, 10);

  // Load sites and locations on component mount (using mock data)
  useEffect(() => {
    if (!isFetchingSitesRef.current) {
      isFetchingSitesRef.current = true;

      const loadSitesAndLocations = async () => {
        try {
          setLoading(true);
          const sitesResponse = await getSitesApi();
          setSitesData(sitesResponse);
          setError(null);
        } catch (err) {
          console.error('Failed to load sites and locations:', err);
          setError(AppLabels.outageLabels.failedToLoadSites);
        } finally {
          setLoading(false);
          isFetchingSitesRef.current = false;
        }
      };

      loadSitesAndLocations();
    }
  }, []);

  // Load outage devices on initial page load with allsites=true
  useEffect(() => {
    const loadInitialOutageDevices = async () => {
      try {
        setDevicesLoading(true);
        const params = {
          allsites: true,
          start_date: from,
          end_date: to
        };
        const devicesResponse = await getOutageDevices(params);
        const outageCountResponse = await getOutageCount(params);
        setDevicesData(devicesResponse);
        setOutageCountData(outageCountResponse);
        setCurrentSearchParams({
          siteId: 'all',
          locationId: 'all'
        });
        setError(null);
      } catch (err) {
        console.error('Failed to load outage devices:', err);
        setError(AppLabels.outageLabels.failedToLoadDevices);
      } finally {
        setDevicesLoading(false);
      }
    };

    // Only load devices after sites data is loaded
    if (sitesData.AllSites.length > 0 || !loading) {
      loadInitialOutageDevices();
    }
  }, [sitesData, loading]);
console.log(sitesData)
  // Get available locations for selected site
  const availableLocations = useMemo(() => {
    if (siteId === 'all') {
      return sitesData.AllSites.flatMap(site => site.locations || []);
    }
    const selectedSite = sitesData.AllSites.find(site => site.store_id === siteId);
    return selectedSite ? selectedSite.locations || [] : [];
  }, [sitesData, siteId]);

  // Transform device data to outage format using API response
  const rows = useMemo(() => {
    return devicesData.devices.map(device => {
      const numericMinutes = parseOutageDurationToMinutes(device.outage_duration_minutes);
      const dualSupplyDevices = device.dualcriticaldevices;
      const allCriticalDevices = [...(device.criticaldevices || []), ...(dualSupplyDevices || [])]
      const criticalDevicesList = allCriticalDevices.map(cd => cd.critical_id || cd.dual_critical_id).filter(id => id !== null && id !== undefined).join(', ') || AppLabels.notApplicable;

      return {
        plugId: device.store_plug_id || AppLabels.notApplicable,
        site: device.store_name || AppLabels.notApplicable,
        location: device.location_name || AppLabels.notApplicable,
        deviceMac: device.device_mac.toLowerCase() || AppLabels.notApplicable,
        deviceName: device.sp_DeviceName.toUpperCase() ?? AppLabels.notApplicable,
        model: device.model || AppLabels.notApplicable,
        isModem: device.isModem,
        modemId: device.modemId || AppLabels.notApplicable,
        smartPlugLocation: device.smart_plug_location || AppLabels.notApplicable,
        statusNow: device.status.toLowerCase() === AppLabels.outageLabels.online ? AppLabels.outageLabels.online : AppLabels.outageLabels.offline || AppLabels.notApplicable,
        criticalDeviceList: criticalDevicesList,
        criticalDevices: device.criticaldevices || [],
        dualSupplyDevices: dualSupplyDevices || [],
        outageMinutes: numericMinutes, // Rounded to 2 decimal places for calculations
        outageDisplay: formatOutageDuration(device.outage_duration_minutes, FORMAT_TYPES.VERBOSE), // Formatted as "d h m s"
        offlineEvents: device.offline_events_count || 0,
      };

    }).sort((a, b) => {
      // Sort by statusNow: offline devices first, then online devices
      if (a.statusNow === 'offline' && b.statusNow === 'online') return -1;
      if (a.statusNow === 'online' && b.statusNow === 'offline') return 1;
      // If both have same status, keep original order
      return 0; // Keep original order for devices with same status
    });
  }, [devicesData.devices]);

  // KPI sums
  const totalMin = rows.reduce((m, r) => m + r.outageMinutes, 0);
  
  // // Find worst device (device with maximum outage time, with tie-breaker)
  // const worstDevice = rows.reduce((worst, current) => {
  //   if (!worst) return current;
  //   if (current.outageMinutes > worst.outageMinutes) return current;
  //   if (current.outageMinutes === worst.outageMinutes) {
  //     // Tie-breaker: prioritize offline devices, then by device name
  //     if (current.statusNow === 'offline' && worst.statusNow !== 'offline') return current;
  //     if (current.statusNow === worst.statusNow) {
  //       return current.deviceName > worst.deviceName ? current : worst;
  //     }
  //   }
  //   return worst;
  // }, null);

  //  // Find least affected device (device with minimum outage time, with tie-breaker)
  // const leastDevice = rows.reduce((least, current) => {
  //   if (!least) return current;
  //   if (current.outageMinutes < least.outageMinutes) return current;
  //   if (current.outageMinutes === least.outageMinutes) {
  //     // Tie-breaker: prioritize online devices, then by device name
  //     if (current.statusNow === 'online' && least.statusNow !== 'online') return current;
  //     if (current.statusNow === least.statusNow) {
  //       return current.deviceName < least.deviceName ? current : least;
  //     }
  //   }
  //   return least;
  // }, null);
  
  // KPI data array for cleaner rendering
  const kpiData = [
    {
      id: 'total-outage',
      label: 'Total Outage Time',
      value: formatMinutesToDuration(totalMin),
      description: "All devices, selected range",
      borderColor: "#b8730b"
    },
    {
      id: 'devices-events',
      label: 'Devices With Events',
      value: rows.length,
      description: "of monitored",
      borderColor: "#1e6fd9"
    },
    // {
    //   id: 'worst-device',
    //   label: rows.length === 1 ? 'Current Device' : 'Worst Device',
    //   value: worstDevice?.deviceName || 'N/A',
    //   description: rows.length === 1 
    //     ? (worstDevice?.statusNow === 'offline' ? 'Currently offline' : 'Currently online')
    //     : (worstDevice?.outageDisplay && worstDevice.outageDisplay !== '0' 
    //        ? `${worstDevice.outageDisplay} down` 
    //        : (worstDevice?.statusNow === 'offline' ? 'Currently offline' : 'No outages recorded')),
    //   borderColor: "#cf3636"
    // },
    // {
    //   id: 'least-affected',
    //   label: rows.length === 1 ? 'Device Status' : 'Least Affected Device',
    //   value: rows.length === 1 
    //     ? (leastDevice?.statusNow === 'offline' ? 'Offline' : 'Online')
    //     : (leastDevice?.deviceName || 'N/A'),
    //   description: rows.length === 1 
    //     ? `Location: ${leastDevice?.location || 'Unknown'}`
    //     : (leastDevice?.outageDisplay && leastDevice.outageDisplay !== '0'
    //        ? `${leastDevice.outageDisplay} down` 
    //        : (leastDevice?.statusNow === 'online' ? 'Currently online' : `Site: ${leastDevice?.site || 'Unknown'}`)),
    //   borderColor: "#1aaff4"
    // }
  ];

  // Search function for when user clicks search button
  const handleSearch = async () => {
    try {
      setDevicesLoading(true);
      const params = {
        start_date: from,
        end_date: to
      };

      // Add site and location filters if selected
      if (siteId !== 'all') {
        params.store_id = siteId;
      }
      if (locationId !== 'all') {
        params.location_id = locationId;
      }
      if (siteId === 'all' && locationId === 'all') {
        params.allsites = true;
      }

      const devicesResponse = await getOutageDevices(params);
       const outageCountResponse = await getOutageCount(params);
        setDevicesData(devicesResponse);
        setOutageCountData(outageCountResponse);
      
      // Update current search params after successful API call
      setCurrentSearchParams({
        siteId: siteId,
        locationId: locationId
      });
      setError(null);
    } catch (err) {
      console.error('Failed to search outage devices:', err);
      setError(AppLabels.outageLabels.failedToSearchDevices);
    } finally {
      setDevicesLoading(false);
    }
  };

  // Location data aggregation
  const locationData = rows.reduce((acc, row) => {
    const existingDataPoint = acc.find((dp) => dp.label === row.location);
    if (existingDataPoint) {
      existingDataPoint.value += row.outageMinutes;
    } else {
      acc.push({
        label: row.location,
        value: row.outageMinutes,
      });
    }
    return acc;
  }, []);

  // Device data aggregation
  const deviceData = rows.reduce((acc, row) => {
    const existingDataPoint = acc.find((dp) => dp.label === row.deviceName);
    if (existingDataPoint) {
      existingDataPoint.value += row.outageMinutes;
    } else {
      acc.push({
        label: row.deviceName,
        value: row.outageMinutes,
      });
    }
    return acc;
  }, []);

  // Get available locations for current search params (for context text)
  const currentAvailableLocations = useMemo(() => {
    if (currentSearchParams.siteId === 'all') {
      return sitesData.AllSites.flatMap(site => site.locations || []);
    }
    const selectedSite = sitesData.AllSites.find(site => site.store_id === currentSearchParams.siteId);
    return selectedSite ? selectedSite.locations || [] : [];
  }, [sitesData, currentSearchParams.siteId]);

  // Generate context text for the table title based on current search results
  const contextText = useMemo(() => {
    return getOutageSearchContextText(currentSearchParams.siteId, currentSearchParams.locationId, sitesData.AllSites, currentAvailableLocations, rows.length);
  }, [currentSearchParams.siteId, currentSearchParams.locationId, sitesData.AllSites, currentAvailableLocations, rows.length]);

  const getTextFromSelected = () => {
     const { siteId, locationId } = currentSearchParams;
    
    // All sites, all locations
    if (siteId === 'all' && locationId === 'all') {
      return 'All Locations for';
    }
    
    // Specific site, all locations in that site  
    if (siteId !== 'all' && locationId === 'all') {
      const siteName = sitesData.AllSites.find(s => s.store_id === siteId)?.store_name || 'Selected Site';
      return `${siteName} - All Locations for`;
    }
    
    // Specific site and specific location
    if (siteId !== 'all' && locationId !== 'all') {
      const siteName = sitesData.AllSites.find(s => s.store_id === siteId)?.store_name || 'Selected Site';
      const locationName = currentAvailableLocations.find(l => l.location_id === locationId)?.location_name || 'Selected Location';
      return `${siteName} - ${locationName} for`;
    }
    
    // All sites, specific location (edge case)
    if (locationId !== 'all') {
      const locationName = currentAvailableLocations.find(l => l.location_id === locationId)?.location_name || 'Selected Location';
      return `${locationName} for`;
    }
    
    return 'Selected Location for';
  }


  return (
    <OutageContainer>
      <OutageOuterContainer>
        <OutagePageHeader>
          <Typography variant="h2" component="h2">
            {AppLabels.outageLabels.pageTitle}
          </Typography>
          <Typography className="subtitle" component="p">
            {AppLabels.outageLabels.pageDescription}
          </Typography>
        </OutagePageHeader>

        {/* Loading and Error States */}
        {loading && (
          <OutagePanel>
            <OutageLoadingMessage>{AppLabels.outageLabels.loadingSitesAndLocations}</OutageLoadingMessage>
          </OutagePanel>
        )}

        {error && (
          <OutagePanel>
            <OutageErrorMessage>{AppLabels.outageLabels.errorPrefix}{error}</OutageErrorMessage>
          </OutagePanel>
        )}

        {/* Filters */}
        {!loading && !error && (
          <OutageFiltersPanel>
            <OutageFiltersRow>
              <OutageFiltersLeft>
                {/* Site Filter */}
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: '150px' }}>

                  <OutageFilterLabel variant="caption">
                    SITE
                  </OutageFilterLabel>

                  <OutageSelect size="small">
                    <Select
                      value={siteId}
                      onChange={e => { setSiteId(e.target.value); setLocationId('all'); }}
                      displayEmpty
                      sx={{
                        '& .MuiInputBase-root': {
                          height: '40px',
                        }
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            backgroundColor: '#fafafa',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            marginTop: '4px',
                          }
                        }
                      }}
                    >
                      <OutageMenuItem value="all">{AppLabels.outageLabels.allSites}</OutageMenuItem>
                      {sitesData.AllSites.map(site => (
                        <OutageMenuItem key={site.store_id} value={site.store_id}>{site.store_name}</OutageMenuItem>
                      ))}
                    </Select>
                  </OutageSelect>
                </Box>

                {/* Location Filter */}
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: '150px' }}>

                  <OutageFilterLabel variant="caption">
                    LOCATION
                  </OutageFilterLabel>
                  <OutageSelect size="small">
                    <Select
                      value={locationId}
                      onChange={e => setLocationId(e.target.value)}
                      displayEmpty
                      sx={{
                        '& .MuiInputBase-root': {
                          height: '40px',
                        }
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            backgroundColor: '#fafafa',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            marginTop: '4px',
                          }
                        }
                      }}
                    >
                      <OutageMenuItem value="all">{AppLabels.outageLabels.allLocations}</OutageMenuItem>
                      {availableLocations.map(location => {
                        const store = sitesData.AllSites.find(site =>
                          site.locations?.some(loc => loc.location_id === location.location_id)
                        );
                        const storeName = store ? store.store_name : AppLabels.outageLabels.unknownStore;
                        return (
                          <OutageMenuItem key={location.location_id} value={location.location_id}>
                            {storeName} - {location.location_name}
                          </OutageMenuItem>
                        );
                      })}
                    </Select>
                  </OutageSelect>
                </Box>

                {/* Start Date Filter */}
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: '150px' }}>

                  <OutageFilterLabel variant="caption">
                    START DATE
                  </OutageFilterLabel>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={from ? dayjs(from) : null}
                      onChange={(newValue) => {
                        if (newValue) {
                          setFrom(newValue.format('YYYY-MM-DD'));
                        } else {
                          setFrom('');
                        }
                      }}
                      minDate={dayjs(minFromDate)}
                      maxDate={dayjs(maxToDate)}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: {
                            width: '150px',
                            minWidth: '150px',
                            '& .MuiInputBase-root': {
                              height: '40px',
                            }
                          }
                        },
                        actionBar: {
                          actions: ['cancel', 'today'],
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>

                {/* End Date Filter */}
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: '150px' }}>
                  <OutageFilterLabel variant="caption">
                    END DATE
                  </OutageFilterLabel>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={to ? dayjs(to) : null}
                      onChange={(newValue) => {
                        if (newValue) {
                          setTo(newValue.format('YYYY-MM-DD'));
                        } else {
                          setTo('');
                        }
                      }}
                      minDate={dayjs(minFromDate)}
                      maxDate={dayjs(maxToDate)}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: {
                            width: '150px',
                            minWidth: '150px',
                            '& .MuiInputBase-root': {
                              height: '40px',
                            }
                          }
                        },
                        actionBar: {
                          actions: ['cancel', 'today'],
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>

  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: '150px' }}>
    <Box sx={{ height: '20px' }}></Box>
<OutagePrimaryButton
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                disabled={devicesLoading}
              >
                {devicesLoading ? AppLabels.outageLabels.searching : AppLabels.outageLabels.search}
              </OutagePrimaryButton>
              </Box>
              </OutageFiltersLeft>
              
              {/* <StyledDateFilters>
                <StyledDateField
                  label="From"
                  type="date"
                  size="small"
                  value={from}
                  onChange={e => setFrom(e.target.value)}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
                <StyledDateField
                  label="To"
                  type="date"
                  size="small"
                  value={to}
                  onChange={e => setTo(e.target.value)}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
              </StyledDateFilters> */}
            </OutageFiltersRow>
            <OutageRangeText>
              {AppLabels.outageLabels.rangePrefix}{new Date(from).toLocaleDateString()}{AppLabels.outageLabels.rangeArrow}{new Date(to).toLocaleDateString()}
            </OutageRangeText>
          </OutageFiltersPanel>
        )}

        {/* KPIs */}
        {/* {!loading && !error && !devicesLoading && (
          <OutageKpisContainer>
            <Kpi
              label={AppLabels.outageLabels.totalOutageMinutes}
              value={formatMinutesToDuration(totalMin)}
              borderColor="#b8730b"
            />
            <Kpi
              label={AppLabels.outageLabels.devicesCount}
              value={rows.length}
              borderColor="#2196f3"
            />
             <Kpi
              label={`Power Outage happened (in days) for ${getTextFromSelected()} selected date range. Please refer the below table for more details.`}
              value={outageCountData.numberOfDays || 0}
              borderColor="#cf3636"
            />
             </OutageKpisContainer>
        )} */}


{!loading && !error && !devicesLoading && (
          <Box 
            sx={{ 
              display: 'flex',
              gap: '16px',
              marginBottom: '22px',
              width: '100%',
              '& > *': {
                flex: '1 1 0', // Equal width for all 4 items
                minWidth: 0,
              }
            }}
          >
            {kpiData.map((kpi) => (
              <Kpi 
                key={kpi.id}
                label={kpi.label}
                value={kpi.value}
                description={kpi.description}
                borderColor={kpi.borderColor}
              />
            ))}
          </Box>
        )}

        {!loading && !error && !devicesLoading && (
          <OutageKpisContainer>
         <OutageShellyHeader>
  <OutageShellyLeftText>
    <Typography variant="body2">
      PDU / Shelly Plug Outages
    </Typography>
  </OutageShellyLeftText>
  
  {/* <OutageShellyRightKpis>
    <Kpi
      label={AppLabels.outageLabels.totalOutageMinutes}
      value={formatMinutesToDuration(totalMin)}
      borderColor="#b8730b"
      isFromDashboard={true}
    />
  </OutageShellyRightKpis> */}
</OutageShellyHeader>
  {outageCountData.sites && outageCountData.sites.length > 0 ? (
    <Box>
      <Typography className="subtitle" component="p" sx={{ marginBottom: '8px' }}>
        Power Outage Summary for selected date range:
      </Typography>
      <Box component="ul" sx={{ 
        margin: 0, 
        paddingLeft: '20px', 
        listStyle: 'disc',
        '& li': {
          color: 'rgb(94, 110, 126)',
          fontSize: '14px',
          fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
          marginBottom: '4px'
        }
      }}>
        {outageCountData.sites.map((site, index) => (
          <li key={`${site.store_id}-${site.location_id}-${index}`}>
            <strong>{site.store_name}</strong> - {site.location_name} experienced {site.number_of_days} day{site.number_of_days > 1 ? 's' : ''} power outage.
          </li>
        ))}
      </Box>
      <Typography component="p" sx={{ marginTop: "8px", fontSize: "13px", fontStyle: "italic", textAlign:"right", color: "red !important" }}>
        {/* Total: {outageCountData.total_days || 0} days across {outageCountData.total_sites || 0} sites.  */}
        * Please refer the below table for more details.
      </Typography>
    </Box>
  ) : (
    <Typography className="subtitle" component="p">
      No power outage events found for the selected date range.
    </Typography>
  )}

          </OutageKpisContainer>
        )}

        {/* Devices Loading State */}
        {devicesLoading && (
          <OutagePanel>
            <OutageLoadingMessage>{AppLabels.outageLabels.loadingDevices}</OutageLoadingMessage>
          </OutagePanel>
        )}

        {/* Outage Distribution Chart */}
        {/* {!loading && !error && !devicesLoading && rows.length > 0 && (
          <OutageDistributionChart locationData={locationData} deviceData={deviceData} />
        )} */}

        {/* Outage Count Message */}
        {/* {!loading && !error && !devicesLoading && (
          <OutagePanel>
            <Typography variant="body1" sx={{
              fontSize: '1rem',
              textAlign: 'left',
              color: '#1e2d5a',
              fontWeight: 500,
            }}>
              {`${outageCountData.count || 0} Power Outage Events happened for ${currentSearchParams.siteId !== 'all'
                ? sitesData.AllSites.find(s => s.store_id === currentSearchParams.siteId)?.store_name || 'Selected Location'
                : 'All Locations'
                } for ${Math.ceil((new Date(to).getTime() - new Date(from).getTime()) / (1000 * 3600 * 24)) + 1} days. Please refer the below table for details.`}
            </Typography>
          </OutagePanel>
        )} */}

        {/* Full table */}
        <OutageTable
          rows={rows}
          loading={loading}
          error={error}
          devicesLoading={devicesLoading}
          startDate={from}
          endDate={to}
          contextText={contextText}
          isDownloadButtonEnabled={user?.isCognizantUser && isNonProdEnvironment()}
          searchParams={{
            allsites: currentSearchParams.siteId === 'all' && currentSearchParams.locationId === 'all',
            start_date: from,
            end_date: to,
            store_id: currentSearchParams.siteId !== 'all' ? currentSearchParams.siteId : undefined,
            location_id: currentSearchParams.locationId !== 'all' ? currentSearchParams.locationId : undefined
          }}
        />
      </OutageOuterContainer>
    </OutageContainer>
  );
}
